import {
  Component,
  Element,
  Event,
  EventEmitter,
  Prop,
  State,
  Watch,
  h,
} from '@stencil/core';
import { HTMLStencilElement, Listen } from '@stencil/core/internal';
import { store } from '@stencil/redux';
import {
  Constants,
  Language,
  PreferredMethod,
  SigningMethod,
  SourceName,
} from '../../enums/const-enums';
import { initConfig } from '../../redux/actions/config-actions';
import { initMethods } from '../../redux/actions/method-action';
import { RootState, initialStore } from '../../redux/store';
import { BrwCookService } from '../../service/brw-cook';
import {
  ApplicationData,
  EventName,
  getEvent,
  getStep,
  pageViewEventName,
  sendEvent,
} from '../../service/piano-analytics';
import {
  clickEventName,
  eventSuccess,
} from '../../service/piano-analytics/piano-analytics.constants';
import { PreLoginService } from '../../service/pre-login';
import { SignInit } from '../../service/sign-init';
import { UrlReloadService } from '../../service/url-reload-service';
import { nativeBridgeHandshake } from '../../utils/handshake';
import { bootstrapFontScale } from '../../utils/scaling';
import {
  getBaseUrl,
  getCookieValue,
  getDefaultMethod,
  logd,
  postMessageToParent,
  setCookieIfAbsentAndUrlParameterPresent,
  setDocumentLanguage,
} from '../../utils/utils';
import {
  RassSignTranslations,
  rassSignTranslations,
} from './i18n/rass-sign.i18n';

@Component({
  tag: 'rass-sign',
  styleUrl: 'rass-sign.scss',
  shadow: true,
})
export class RassSignComponent {
  @Element() element!: HTMLStencilElement;
  @Prop() absPath: string;
  @Prop() assetsPath = '/s-t-a-t-i-c/msp/authentication/v2/vrs_4820747/assets/';
  @Prop() preLogin = false;
  @State() language: Language;
  @State() analyticsSigningMethod: SigningMethod;
  @Prop() sourceName: SourceName = SourceName.OTHER;
  @Prop() tinWebRedirectUrl = '';
  @Prop() acceptedMethods = [
    SigningMethod.RABO_SCANNER,
    SigningMethod.RANDOM_READER,
  ].join(' ');
  @Prop({ mutable: true, reflect: true }) preferredMethod: SigningMethod;
  @Prop() hideFooter = false;
  @Prop({ mutable: true, reflect: true }) activeSignMethod: SigningMethod;
  @Prop({ mutable: true, reflect: true }) hasInitialized: boolean = null;
  @Prop({ mutable: true, reflect: true }) hasErrorStatus: string = null;
  @Prop({ mutable: true, reflect: true }) hasSignMethodStatus: string = null;
  @Event() domReady: EventEmitter;
  @Event({ eventName: 'overlayStateChange', bubbles: true })
  overlayStateChange: EventEmitter;
  initConfig!: typeof initConfig;
  initMethods!: typeof initMethods;
  baseUrl = getBaseUrl();

  constructor() {
    new BrwCookService().init();
  }

  componentWillLoad(): void {
    store.setStore(initialStore);
    store.mapDispatchToProps(this, {
      initConfig,
      initMethods,
    });
    store.mapStateToProps(this, (state: RootState) => {
      const {
        config: { language, isLoading },
        methods: { activeSignMethod, activeSignMethodStatus },
        notification: { type },
      } = state;
      return {
        language,
        activeSignMethod,
        hasInitialized: !isLoading,
        hasErrorStatus: type,
        hasSignMethodStatus: activeSignMethodStatus,
      };
    });

    setCookieIfAbsentAndUrlParameterPresent(Constants.METHOD_COOKIE_NAME);
    setCookieIfAbsentAndUrlParameterPresent(Constants.LANGUAGE_COOKIE_NAME);

    if (this.preLogin && !this.preferredMethod) {
      this.preferredMethod = getDefaultMethod();
    }

    this.initConfig({
      absPath: this.absPath,
      assetsPath: this.assetsPath,
      language: getCookieValue(Constants.LANGUAGE_COOKIE_NAME)
        ? (getCookieValue(Constants.LANGUAGE_COOKIE_NAME) as Language)
        : this.language,
      sourceName: this.sourceName,
    });
    this.initMethods({
      allowedSignMethods: this.acceptedMethods.split(' ') as SigningMethod[],
      preferredSignMethod: this.preferredMethod,
    });
    if (
      this.preferredMethod !== SigningMethod.APP &&
      this.preferredMethod !== SigningMethod.USERKEY_TIN_APP
    ) {
      // Only reload on chunk error on web based signing methods to prevent possible double signing
      const reloadService = new UrlReloadService(new URL(window.location.href));
      reloadService.reloadOnChunkError();
    }
    this.bootstrapScaling().catch((error: unknown) =>
      logd(() => console.error(error)),
    );
  }

  sendAnalyticsEvent(name: EventName, event: ApplicationData): void {
    sendEvent(name, event).catch((err: Error) =>
      logd(() => console.error('Error while sending analytics event:', err)),
    );
  }

  @Watch('activeSignMethod')
  sendPageviewEvent(): void {
    if (!this.activeSignMethod || this.analyticsSigningMethod) {
      return;
    }
    this.analyticsSigningMethod = this.activeSignMethod;
    this.sendAnalyticsEvent(
      pageViewEventName,
      getStep(this.sourceName, this.activeSignMethod),
    );
  }

  async componentDidLoad(): Promise<void> {
    try {
      if (this.preLogin) {
        // Since in the pre-login flow the page is cached, we need to determine the language via the cookie and otherwise use a default
        this.language =
          this.language ||
          (getCookieValue('RABOLANG') as Language) ||
          Language.EN;
        const { continueUrl } = await new PreLoginService().doPreLogin(null);
        if (continueUrl) {
          return;
        }
      }
      await new SignInit().doSignInit();
      setDocumentLanguage(this.language);
      //emit event that UI has been rendered (used for Q6)
      this.domReady.emit(true);
      if (window.parent !== window) {
        this.observeHeightChanges();
      } else {
        this.inAppSafariScrollFix();
      }
    } catch (error: unknown) {
      logd(() => console.error(error));
    }
  }

  async bootstrapScaling(): Promise<void> {
    try {
      // If we are not in an iframe, bootstrap font scaling
      if (window === window.top) {
        await nativeBridgeHandshake();
        bootstrapFontScale();
      }
    } catch (error: unknown) {
      logd(() => console.error('Error bootstrapping font scale:', error));
    }
  }

  inAppSafariScrollFix(): void {
    document.documentElement.style.overflow = 'hidden';
    const bodyElement = document.querySelector('body');
    bodyElement.style.overflow = 'auto';
    bodyElement.style.height = '100vh';
  }

  observeHeightChanges(): void {
    if (window === window.parent) {
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      postMessageToParent<AppModel.PageHeightChangeEvent>({
        type: 'sign-height-change',
        value: document.body.scrollHeight,
      });
    });
    resizeObserver.observe(document.body);
  }

  get activeSigningMethodComponent(): void {
    const map: Record<SigningMethod, void> = {
      [SigningMethod.APP]: (<rass-app />) as void,
      [SigningMethod.EOTP]: (<rass-eotp />) as void,
      [SigningMethod.ONE_CLICK_ID]: (<rass-one-click-id />) as void,
      [SigningMethod.OOB]: (<rass-oob />) as void,
      [SigningMethod.QR]: (<rass-qr />) as void,
      [SigningMethod.RABO_SCANNER]: (<rass-rabo-scanner />) as void,
      [SigningMethod.RANDOM_READER]: (<rass-random-reader />) as void,
      [SigningMethod.USERKEY_TIN_APP]: (<rass-app />) as void,
      [SigningMethod.TIN_WEB]: (<rass-tinweb />) as void,
    };
    return map[this.activeSignMethod];
  }

  get preferredSignMethod(): SigningMethod {
    const cookieValue = getCookieValue(
      Constants.METHOD_COOKIE_NAME,
    ) as PreferredMethod;
    if (cookieValue) {
      const map: Record<PreferredMethod, SigningMethod> = {
        [PreferredMethod.EOTP]: SigningMethod.EOTP,
        [PreferredMethod.QR]: SigningMethod.QR,
        [PreferredMethod.RABO_SCANNER]: SigningMethod.RABO_SCANNER,
        [PreferredMethod.RANDOM_READER]: SigningMethod.RANDOM_READER,
      };
      return map[cookieValue] || SigningMethod.RABO_SCANNER;
    }
    return this.preferredMethod;
  }

  get translations(): RassSignTranslations {
    return (
      rassSignTranslations[this.language] || rassSignTranslations[Language.EN]
    );
  }

  @Listen('message', { target: 'window' })
  onMessage(event: MessageEvent<AppModel.SensesSignerMessageData>) {
    if (event.data?.action === 'disable-scroll') {
      document.documentElement.style.overflow = 'hidden';
      postMessageToParent<AppModel.PageHeightChangeEvent>({
        type: 'sign-height-change',
        value: document.body.scrollHeight,
      });
    }
  }

  @Listen('rass:sign-submit:success', { target: 'window' })
  onLoginSuccess(): void {
    this.sendAnalyticsEvent(
      clickEventName,
      getEvent(this.sourceName, this.activeSignMethod, eventSuccess),
    );
  }

  render(): void {
    return (
      <main
        class={{
          'sfc-padding-block-start-200': true,
          'sfc-padding-block-end-100': this.hideFooter,
        }}
      >
        <style>@import url({`'${this.assetsPath}css/sfc-style.css'`});</style>
        <div class={`body-height ${this.activeSignMethod}`}>
          <rass-summary />
          <rass-notification />
          {this.activeSigningMethodComponent}
        </div>
        {this.hideFooter ? (
          ''
        ) : (
          <rass-footer tinWebRedirectUrl={this.tinWebRedirectUrl} />
        )}
        <rass-overlay
          button-label={this.translations.errors.continue}
          assets-path={this.assetsPath}
        />
        {this.hasInitialized ? (
          ''
        ) : (
          <div id="loading">
            <sfc-spinner />
          </div>
        )}
      </main>
    ) as void;
  }
}
